import React, { useState, useEffect, useMemo, useRef } from 'react';
import { X, Loader2, AlertCircle, Search, FileText, ChevronRight, ChevronLeft } from 'lucide-react';
import { pagesApi } from '../../utils/api/pages';

/**
 * Inject a base tag to make links open in parent window, not iframe
 * This prevents the app from rendering inside itself when internal links are clicked
 */
const injectBaseTarget = (html) => {
  if (!html) return html;
  
  // If there's already a <head>, inject base tag at the start of it
  if (html.includes('<head>')) {
    return html.replace('<head>', '<head><base target="_top">');
  }
  // If there's no head but has html tag, add head with base
  if (html.includes('<html')) {
    return html.replace(/<html[^>]*>/, '$&<head><base target="_top"></head>');
  }
  // Otherwise, prepend base tag
  return `<base target="_top">${html}`;
};

/**
 * Wiki-style sidebar for page navigation
 * On mobile: flyout drawer that overlays content
 * On desktop: always visible sidebar
 */
const PageSidebar = ({ 
  pages, 
  currentSlug, 
  onPageSelect, 
  searchQuery, 
  onSearchChange,
  isOpen,
  onClose 
}) => {
  const sidebarRef = useRef(null);
  
  const filteredPages = useMemo(() => {
    if (!searchQuery.trim()) return pages;
    const query = searchQuery.toLowerCase();
    return pages.filter(p => 
      p.name.toLowerCase().includes(query) ||
      p.slug.toLowerCase().includes(query)
    );
  }, [pages, searchQuery]);

  // Handle click outside on mobile
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target)) {
        onClose?.();
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handlePageClick = (slug) => {
    onPageSelect(slug);
    onClose?.(); // Close sidebar on mobile after selection
  };

  return (
    <>
      {/* Mobile overlay backdrop */}
      <div 
        className={`md:hidden fixed inset-0 bg-black/50 transition-opacity z-40
                    ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      />
      
      {/* Sidebar */}
      <div 
        ref={sidebarRef}
        className={`
          w-64 flex-shrink-0 bg-slate-800 border-r border-slate-700 flex flex-col
          md:relative md:translate-x-0 md:opacity-100
          fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        `}
      >
        {/* Mobile close button */}
        <div className="md:hidden flex items-center justify-between p-3 border-b border-slate-700">
          <span className="text-sm font-medium text-slate-300">Navigation</span>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Search */}
        <div className="p-3 border-b border-slate-700">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search pages..."
              className="w-full pl-9 pr-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg 
                         text-sm text-white placeholder-slate-500
                         focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Page List */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-2">
            <div className="text-xs font-medium text-slate-500 uppercase tracking-wider px-2 py-2">
              Pages
            </div>
            {filteredPages.length === 0 ? (
              <div className="px-2 py-4 text-sm text-slate-500 text-center">
                {searchQuery ? 'No pages found' : 'No pages available'}
              </div>
            ) : (
              <div className="space-y-0.5">
                {filteredPages.map(page => (
                  <button
                    key={page.page_id || page.slug}
                    onClick={() => handlePageClick(page.slug)}
                    className={`w-full flex items-center gap-2 px-2 py-2 rounded-lg text-left text-sm
                               transition-colors group
                               ${page.slug === currentSlug 
                                 ? 'bg-blue-600/20 text-blue-400' 
                                 : 'text-slate-300 hover:bg-slate-700/50 hover:text-white'}`}
                  >
                    <FileText size={16} className="flex-shrink-0 opacity-60" />
                    <span className="flex-1 truncate">{page.name}</span>
                    {page.slug === currentSlug && (
                      <ChevronRight size={14} className="flex-shrink-0 opacity-60" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

/**
 * Full-screen page viewer component
 * Displays a page's HTML content with wiki-style sidebar
 * Matches FullscreenWindow styling
 * Mobile: Sidebar is a flyout drawer
 * Desktop: Sidebar is always visible
 */
const PageViewer = ({ pageSlug, onClose, onNavigate }) => {
  const [currentSlug, setCurrentSlug] = useState(pageSlug);
  const [page, setPage] = useState(null);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Fetch all pages for sidebar (include drafts for now, admin can see all)
  useEffect(() => {
    const fetchPages = async () => {
      try {
        const pageList = await pagesApi.list(false); // include unpublished
        setPages(pageList);
      } catch (err) {
        console.error('Failed to load pages list:', err);
      }
    };
    fetchPages();
  }, []);

  // Fetch current page
  useEffect(() => {
    const fetchPage = async () => {
      if (!currentSlug) {
        setError('No page specified');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const pageData = await pagesApi.getBySlug(currentSlug);
        setPage(pageData);
      } catch (err) {
        console.error('Failed to load page:', err);
        setError(err.message || 'Failed to load page');
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [currentSlug]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (sidebarOpen) {
          setSidebarOpen(false);
        } else {
          onClose?.();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, sidebarOpen]);

  // Navigate to different page
  const handlePageSelect = (slug) => {
    setCurrentSlug(slug);
    if (onNavigate) {
      onNavigate(slug);
    }
  };

  return (
    <div 
      className="fixed inset-0 flex justify-center md:backdrop-blur-sm md:bg-black/30
                 animate-in fade-in-0 duration-200"
      style={{ zIndex: 99999 }}
    >
      {/* Mobile: full width, Desktop: 80% width centered */}
      <div className="relative w-full md:w-4/5 h-[100dvh] md:h-screen flex bg-slate-900">
        {/* Wiki Sidebar - flyout on mobile, always visible on desktop */}
        <PageSidebar
          pages={pages}
          currentSlug={currentSlug}
          onPageSelect={handlePageSelect}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden relative">
          {/* Mobile: Floating arrow tab on left edge to open sidebar */}
          <button
            onClick={() => setSidebarOpen(true)}
            className={`md:hidden fixed left-0 top-1/2 -translate-y-1/2 z-30
                       bg-slate-700/90 hover:bg-slate-600 
                       py-4 px-1 rounded-r-lg shadow-lg
                       text-slate-300 hover:text-white transition-all
                       ${sidebarOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
            aria-label="Open navigation"
          >
            <ChevronRight size={20} />
          </button>

          {/* Mobile Header Bar - minimal with just close button */}
          <div className="md:hidden flex-none flex items-center justify-between px-3 py-2 bg-slate-800 border-b border-slate-700">
            <span className="text-sm font-medium text-white truncate">
              {loading ? 'Loading...' : page?.name || 'Page'}
            </span>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 transition-colors"
              aria-label="Close"
            >
              <X size={16} className="text-white" />
            </button>
          </div>

          {/* Desktop Header */}
          <div className="hidden md:flex flex-none items-center px-6 py-4 bg-slate-800/50 border-b border-slate-700">
            <h1 className="text-xl font-semibold text-white">
              {loading ? 'Loading...' : page?.name || 'Page'}
            </h1>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-auto">
            {loading && (
              <div className="flex items-center justify-center h-full">
                <Loader2 size={48} className="animate-spin text-blue-500" />
              </div>
            )}

            {error && (
              <div className="flex flex-col items-center justify-center h-full text-center p-8">
                <AlertCircle size={64} className="text-red-400 mb-4" />
                <h2 className="text-xl font-semibold text-white mb-2">
                  Unable to load page
                </h2>
                <p className="text-slate-400 mb-6">{error}</p>
                <button
                  onClick={onClose}
                  className="px-6 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                >
                  Go Back
                </button>
              </div>
            )}

            {!loading && !error && page && (
              <div className="h-full w-full">
                {/* Render page HTML content in iframe for proper styling isolation */}
                {/* Note: allow-same-origin removed for security - prevents XSS attacks via user-generated content */}
                <iframe
                  srcDoc={injectBaseTarget(page.content) || ''}
                  title={page.name}
                  className="w-full h-full border-0"
                  allow="pointer-lock; clipboard-read; clipboard-write"
                  sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox allow-top-navigation allow-pointer-lock"
                />
              </div>
            )}
          </div>
        </div>

        {/* Desktop Close Button - Outside window like FullscreenWindow */}
        <button
          onClick={onClose}
          className="hidden md:block absolute -right-16 top-4 p-2.5 rounded-full 
                     bg-black/50 hover:bg-black/70 
                     transition-colors group"
          aria-label="Close page viewer (Esc)"
          title="Close (Esc)"
        >
          <X className="w-6 h-6 text-white/70 group-hover:text-white/90 transition-colors" />
        </button>
      </div>
    </div>
  );
};

export default PageViewer;

